import { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, MessageCircle, ChevronDown, Instagram, CalendarPlus } from "lucide-react";
import { Reveal, EASE } from "./motion";
import { PrimaryButton } from "./PrimaryButton";
import { getVisibleDates, getFullSchedule, ALL_DATES } from "../../lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://chat.whatsapp.com/LeRy0lnjVAcH4eDm86BkYa?s=hd&p=i&mlu=4";
const WELCOME_KIT_URL = "https://drive.google.com/file/d/1l4sYWZ9x2HTlKmYDMdnBQaqYuMdPwxr6/view?usp=sharing";
const INSTAGRAM_DM_URL = "https://ig.me/m/iamkristenhinman?ref=w58680175";

// Google Calendar "add event" link for a selected date (8:30–10:30am, Bend/Pacific).
const pad = (n) => String(n).padStart(2, "0");
const calendarUrl = (dateObj) => {
  const day = `${dateObj.getFullYear()}${pad(dateObj.getMonth() + 1)}${pad(dateObj.getDate())}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: "Coffee, Create & Community",
    dates: `${day}T083000/${day}T103000`,
    ctz: "America/Los_Angeles",
    location: "Thump at The Grove, 921 NW Mount Washington Dr, Bend, OR 97703",
    details:
      "An in-person gathering & coworking morning. Bring one real project. Come and go as your day allows.",
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
};

export const RsvpForm = () => {
  const { monthLabel, dates } = useMemo(() => getVisibleDates(), []);
  const schedule = useMemo(() => getFullSchedule(), []);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [error, setError] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);

  const toggle = (label) =>
    setSelected((prev) => {
      if (prev.includes(label)) return prev.filter((x) => x !== label);
      if (prev.length >= 2) return prev; // max 2 dates
      return [...prev, label];
    });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) return setError("Please add your name and email.");
    if (!form.phone.trim()) return setError("Please add your cell number.");
    if (!selected.length) return setError("Pick at least one date you're coming.");
    setStatus("loading");
    try {
      await axios.post(`${API}/rsvp`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        dates: selected,
      });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="rsvp" data-testid="rsvp-section" className="bg-oat px-5 sm:px-8 lg:px-12 py-24 sm:py-32 scroll-mt-6">
      <div className="max-w-[820px] mx-auto">
        <Reveal>
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.24em] text-mushroom-deep">
            who it's for
          </span>
          <p className="mt-4 text-2xl sm:text-3xl font-medium leading-[1.15] text-ink/85">
            women with big visions and big hearts. if you're building something, in transition,
            chasing a creative spark, or you just want a room of people who take your ideas seriously,
            this is for you.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="mt-16 font-display font-black lowercase tracking-tighter leading-[0.88] text-5xl sm:text-6xl lg:text-7xl">
            save my spot
          </h2>
        </Reveal>

        <Reveal delay={0.16}>
          <p className="mt-5 max-w-2xl text-base sm:text-lg font-medium leading-[1.25] text-ink/70" data-testid="rsvp-free-note-top">
            enter your information, pick your date below and i'll email your welcome packet, the
            details, what to bring, and prompts to get you started, right to your inbox. free to RSVP.
          </p>
        </Reveal>

        <AnimatePresence mode="wait">
          {status === "done" ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="mt-10 rounded-[2rem] bg-ink text-oat p-10 sm:p-14 text-center"
              data-testid="rsvp-success"
            >
              <div className="mx-auto w-16 h-16 rounded-full bg-sunbeam flex items-center justify-center">
                <Check className="w-9 h-9 text-ink" strokeWidth={3} />
              </div>
              <p className="mt-6 font-display font-black lowercase text-5xl sm:text-6xl leading-[0.9]">
                you're in!
              </p>
              <p className="mt-6 text-xl sm:text-2xl font-medium leading-[1.3] text-oat" data-testid="rsvp-confirm-dates">
                see you on {selected.join(" & ")}
              </p>
              <p className="mt-4 text-lg sm:text-xl font-medium leading-[1.35] text-oat/85">
                at <span className="font-semibold">THUMP</span> at the grove
                <br />
                921 nw mount washington drive, bend, oregon 97703
              </p>
              <p className="mt-5 text-base font-medium text-oat/70">
                text kristen with any question 458-329-8464
              </p>

              <div className="mt-7 flex flex-col items-center gap-2" data-testid="rsvp-calendar-links">
                {selected.map((label) => {
                  const entry = ALL_DATES.find((d) => d.label === label);
                  if (!entry) return null;
                  return (
                    <a
                      key={label}
                      href={calendarUrl(entry.date)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`rsvp-calendar-${entry.id}`}
                      className="inline-flex items-center gap-2 text-oat/85 hover:text-oat underline underline-offset-4 decoration-oat/40 font-semibold transition-colors"
                    >
                      <CalendarPlus className="w-4 h-4" strokeWidth={2.4} />
                      add {label} to calendar
                    </a>
                  );
                })}
              </div>

              <a
                href={WELCOME_KIT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rsvp-welcome-kit"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-sunbeam text-ink font-display font-black lowercase tracking-tight rounded-full px-8 py-4 text-lg leading-none hover:scale-[1.03] transition-transform"
              >
                read the welcome kit
              </a>
              <div className="mt-5">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="rsvp-whatsapp"
                  className="inline-flex items-center justify-center gap-2 text-oat/80 underline underline-offset-4 font-semibold hover:text-oat transition-colors"
                >
                  <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                  join the whatsapp group
                </a>
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="mt-10 rounded-[2rem] bg-mushroom-light p-6 sm:p-10 space-y-7"
            >
              <div>
                <label htmlFor="rsvp-name" className="block text-sm font-semibold tracking-[0.02em] text-ink/70 mb-2">
                  name
                </label>
                <input
                  id="rsvp-name"
                  data-testid="rsvp-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-oat rounded-xl px-5 py-4 text-lg font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
                  placeholder="your name"
                />
              </div>

              <div>
                <label htmlFor="rsvp-email" className="block text-sm font-semibold tracking-[0.02em] text-ink/70 mb-2">
                  email
                </label>
                <input
                  id="rsvp-email"
                  data-testid="rsvp-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full bg-oat rounded-xl px-5 py-4 text-lg font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
                  placeholder="you@email.com"
                />
              </div>

              <div>
                <label htmlFor="rsvp-phone" className="block text-sm font-semibold tracking-[0.02em] text-ink/70 mb-1">
                  cell number
                </label>
                <p className="text-sm font-medium text-ink/55 mb-2 leading-[1.2]">
                  (to be alerted of any schedule changes &amp; to be added to the whatsapp group)
                </p>
                <input
                  id="rsvp-phone"
                  data-testid="rsvp-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  className="w-full bg-oat rounded-xl px-5 py-4 text-lg font-medium text-ink outline-none border-2 border-transparent focus:border-ink transition-colors"
                  placeholder="(555) 555-5555"
                />
              </div>

              <div>
                <p className="text-sm font-semibold tracking-[0.02em] text-ink/70 mb-1">
                  which date are you coming?
                </p>
                <p className="text-sm font-medium text-ink/55 mb-3 lowercase">
                  {dates.length === 0
                    ? "no dates are open right now — see the full season schedule below"
                    : `${monthLabel ? `${monthLabel} dates · ` : ""}pick 1 or 2`}
                </p>
                <div className="space-y-3" data-testid="rsvp-dates">
                  {dates.map((d) => {
                    const active = selected.includes(d.label);
                    const disabled = !active && selected.length >= 2;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        data-testid={`rsvp-date-${d.id}`}
                        onClick={() => toggle(d.label)}
                        className={`w-full flex items-center gap-4 text-left rounded-xl px-5 py-4 border-2 transition-colors ${
                          active ? "bg-sunbeam border-ink" : "bg-oat border-transparent hover:border-mushroom"
                        } ${disabled ? "opacity-40" : ""}`}
                      >
                        <span
                          className={`w-6 h-6 rounded-md shrink-0 flex items-center justify-center border-2 ${
                            active ? "bg-ink border-ink" : "border-ink/40"
                          }`}
                        >
                          {active && <Check className="w-4 h-4 text-sunbeam" strokeWidth={3.5} />}
                        </span>
                        <span className="text-base sm:text-lg font-medium text-ink leading-tight">{d.label}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-4 border-t border-ink/10 pt-4">
                  <button
                    type="button"
                    data-testid="rsvp-schedule-toggle"
                    onClick={() => setShowSchedule((s) => !s)}
                    className="w-full flex items-center justify-between gap-2 text-left text-sm font-semibold text-ink/70 hover:text-ink transition-colors"
                  >
                    <span>see the full season schedule</span>
                    <ChevronDown
                      className={`w-5 h-5 shrink-0 transition-transform duration-300 ${showSchedule ? "rotate-180" : ""}`}
                      strokeWidth={2.4}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {showSchedule && (
                      <motion.div
                        key="schedule"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="overflow-hidden"
                        data-testid="rsvp-schedule"
                      >
                        <div className="pt-4 space-y-4">
                          {schedule.map((g) => (
                            <div key={g.key}>
                              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-mushroom-deep">
                                {g.monthLabel}
                              </p>
                              <ul className="mt-1.5 space-y-1">
                                {g.dates.map((d) => (
                                  <li
                                    key={d.id}
                                    className="text-base font-medium leading-tight text-ink/80"
                                  >
                                    {d.label}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <a
                  href={INSTAGRAM_DM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="rsvp-instagram-dm"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ink/70 hover:text-ink underline underline-offset-4 decoration-mushroom transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={2.4} />
                  have additional questions? send me a DM on instagram
                </a>
              </div>

              {error && (
                <p className="text-base font-semibold text-red-700" data-testid="rsvp-error">
                  {error}
                </p>
              )}

              <PrimaryButton
                type="submit"
                testId="rsvp-submit"
                withArrow={false}
                className="w-full"
              >
                {status === "loading" ? "saving…" : "save my spot"}
              </PrimaryButton>
              <p className="text-center text-sm font-semibold text-mushroom-deep" data-testid="rsvp-free-note">
                free to RSVP
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
